import { useEffect, useRef } from "react";

import type { ProcessingStatus } from "../services/ArtifactEnrichmentService";

/**
 * Calls `sync` whenever the selected documents' statuses change.
 *
 * Repeated status results are skipped until the selected SDR set changes;
 * any change, including to an empty selection, re-arms the sync, so a
 * document selected again gets its status written even when it is unchanged.
 */
export const useStatusSyncOnChange = (
  selectedSdrIds: number[],
  statuses: ProcessingStatus[],
  sync: (statuses: ProcessingStatus[]) => void,
) => {
  const lastStatusKey = useRef("");
  const selectionKey = [...selectedSdrIds].sort((a, b) => a - b).join(",");

  // Declared before the sync effect so a reset lands first in the same commit.
  useEffect(() => {
    lastStatusKey.current = "";
  }, [selectionKey]);

  useEffect(() => {
    if (selectedSdrIds.length === 0 || statuses.length === 0) return;
    const statusKey = statuses
      .map(
        (s) =>
          `${s.source_data_record_id}:${s.progress_percentage}:${s.is_processing}`,
      )
      .sort()
      .join(",");
    if (statusKey === lastStatusKey.current) return;
    lastStatusKey.current = statusKey;
    sync(statuses);
  }, [selectedSdrIds, statuses, sync]);
};
